import { AxiosError, AxiosRequestConfig } from 'axios'
import { get, partition, uniqBy } from 'lodash'
import Config from 'react-native-config'
import { Bugsnag, Apollo, Env } from '@config'
import {
  NutritionixItemWithNutrients,
  NutritionixSearchResults,
  NutritionixWithNutrientsResults,
} from '@services/Nutritionix/types'
import { isBrandedIngredient, sanitizeNaturalLanguageQuery } from '@services/Nutritionix/utils'
import { createThirdPartyApiClient } from '@services/thirdPartyApiClient'
import { Storage } from '@utils'
import { Login } from '@src/models/app.types'

// Nutritionix API Docs
// https://docs.google.com/document/d/1_q-K-ObMTZvO0qUEAxROrN3bwMujwAN25sLHwJzliK0

const DIRECT_BASE_URL = 'https://trackapi.nutritionix.com/v2'
const PROXY_URLS: Partial<Record<Env, string>> = {
  production: 'https://nutritionix.nutrisense.io/v2',
  staging: 'https://nutritionix-staging.nutrisense.io/v2',
}

// Requests go through our nutritionix-proxy Cloudflare Worker, which validates
// the user's auth token and injects the Nutritionix credentials server-side.
// The proxy must match the backend the app is CURRENTLY logged into — the admin
// endpoint switch (Apollo.resetEndpoint) can point a build at the other
// backend at runtime, and tokens only validate against the introspection
// endpoint of the backend that issued them. Apollo.endpointEnv tracks the
// resolved backend env, so the proxy URL follows it per request.
//
// Development (localhost backend, no proxy in front of it) calls Nutritionix
// directly with the credentials from .env.development.
const getProxyBaseUrl = () => PROXY_URLS[Apollo.endpointEnv] ?? null

const client = createThirdPartyApiClient({
  headers: {
    ...(Config.NUTRITIONIX_APPLICATION_ID
      ? {
          'x-app-id': Config.NUTRITIONIX_APPLICATION_ID,
          'x-app-key': Config.NUTRITIONIX_APPLICATION_KEYS,
        }
      : {}),
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

const getDefaultOptions = () => {
  const proxyBaseUrl = getProxyBaseUrl()

  if (proxyBaseUrl) {
    // The proxy authenticates us with the same Bearer token the GraphQL API
    // uses and computes x-remote-user-id server-side. Omit the header entirely
    // when logged out so the proxy sees a missing token, not a malformed one.
    const token = get(Storage.get<Login>('login'), 'token')

    return {
      baseURL: proxyBaseUrl,
      headers: token ? { authorization: `Bearer ${token}` } : {},
    } as AxiosRequestConfig
  }

  return {
    baseURL: DIRECT_BASE_URL,
    // Nutritionix requires 0 for development requests. Production requests
    // go through the proxy, which derives and hashes the user ID server-side.
    headers: { 'x-remote-user-id': 0 },
  } as AxiosRequestConfig
}

// Gateway-level failures in front of Nutritionix (proxy cold start, upstream
// hiccup) come back fast and usually succeed on a second attempt, so one retry
// turns most of them into a working screen instead of an empty ingredient
// list. Deliberately excluded: 500 (Nutritionix's catch-all for a query it
// can't parse — retrying never helps) and timeouts (the connection is already
// struggling; retrying only adds to the pile).
const RETRYABLE_STATUSES = new Set([502, 503, 504])
const RETRY_BASE_DELAY_MS = 400
const MAX_RETRIES = 1

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// One user action can fan out into dozens of lookups, and a single root cause
// (proxy blip, saturated network) fails all of them. Reporting every failure
// buries the signal — 600+ events a week for what is really a handful of
// incidents — so report the first occurrence of each endpoint+status per
// session and stay quiet about the repeats.
const reportedFailures = new Set<string>()

const reportFailure = (error: AxiosError, url: string, status: number) => {
  const key = `${url.split('?')[0]}:${status}`

  if (reportedFailures.has(key)) {
    return
  }

  reportedFailures.add(key)

  Bugsnag.notify(error, (event) => {
    // eslint-disable-next-line no-param-reassign
    event.severity = 'info'
    event.addMetadata('nutritionix', { url, status })
  })
}

const request = async <T>(
  options: AxiosRequestConfig,
  { showNotFoundWarnings = true, attempt = 0 } = {},
): Promise<T | undefined> => {
  const onError = (error: AxiosError) => {
    if (error.response) {
      if (error.response.status === 404 && !showNotFoundWarnings) {
        return
      }

      console.warn('Data:', error.response.data)
      console.warn('Status:', error.response.status)
      console.warn('Headers:', error.response.headers)
    } else {
      console.warn('Error Message:', error.message)
    }
  }

  try {
    Bugsnag.leaveBreadcrumb('nutritionix', { options }, 'request')

    // Recover from a transient keychain read failure at startup so the proxy request isn't sent
    // without its Bearer token (mirrors the Apollo auth link). Only the proxy path uses the
    // token; direct/development requests don't, so don't make them wait on the token store.
    if (getProxyBaseUrl()) {
      await Storage.ensureTokensLoaded()
    }

    const defaults = getDefaultOptions()
    const opts = { ...defaults, ...options }

    const response = await client.request<T>(opts)
    const { data } = response

    if (data || response.status === 204) {
      return data
    }

    onError({ response } as AxiosError)

    return undefined
  } catch (error: any) {
    const status = error.response?.status

    if (RETRYABLE_STATUSES.has(status) && attempt < MAX_RETRIES) {
      // Jitter so a screen that fanned out several lookups doesn't retry them
      // all in the same tick.
      await delay(RETRY_BASE_DELAY_MS * (attempt + 1) + Math.random() * RETRY_BASE_DELAY_MS)

      return request<T>(options, { showNotFoundWarnings, attempt: attempt + 1 })
    }

    // Skip reporting failures we can't act on: requests that never got a
    // response (offline, timeouts), 404s (unknown barcode/item), and 400s
    // (Nutritionix rejects some user-typed queries, e.g. empty or "+").
    if (error.response && status !== 404 && status !== 400) {
      reportFailure(error, options.url ?? '', status)
    }

    onError(error)

    return undefined
  }
}

// The ingredient screens ask for the same nutrient payloads over and over:
// every keystroke re-runs the recent-ingredient refresh across the same
// descriptions, and each visit to the screen starts from scratch. Nutrient
// data for a given query or item id is stable, so answering from memory
// removes most of the fan-out before it reaches the network.
const NUTRIENTS_CACHE_LIMIT = 200
const nutrientsCache = new Map<string, NutritionixItemWithNutrients[]>()

const readNutrientsCache = (key: string) => {
  const cached = nutrientsCache.get(key)

  if (!cached) {
    return undefined
  }

  // Re-insert so the least recently used entry is the one evicted below.
  nutrientsCache.delete(key)
  nutrientsCache.set(key, cached)

  return cached
}

const writeNutrientsCache = (key: string, items: NutritionixItemWithNutrients[]) => {
  nutrientsCache.set(key, items)

  if (nutrientsCache.size > NUTRIENTS_CACHE_LIMIT) {
    const oldestKey = nutrientsCache.keys().next().value

    if (oldestKey) {
      nutrientsCache.delete(oldestKey)
    }
  }
}

const searchItems = async (query: string) => {
  // This endpoint returns 400 if query is invalid (empty string, some special characters like "+")
  // Bug on Nutritionix side: query "no" returns no results
  const response = await request<NutritionixSearchResults>({
    method: 'GET',
    url: `/search/instant?query=${query}&self=false&branded=true&detailed=true`,
  })

  if (!response) {
    return null
  }

  // De-duplicate common products by `tag_id`
  // e.g. "apple", "apples" and "apple raw" are the same ingredient
  const commonProducts = uniqBy(response.common, 'tag_id')
  const brandedProducts = response.branded

  // Filter out edge case items without full_nutrients
  const itemsWithNutrients = [...commonProducts, ...brandedProducts].filter(
    (item) => item.full_nutrients,
  )
  const [startsWithQuery, containsQuery] = partition(itemsWithNutrients, (ingredient) => {
    if (isBrandedIngredient(ingredient)) {
      return (
        query.toLowerCase().includes(ingredient.brand_name.toLowerCase()) &&
        query.toLowerCase().includes((ingredient.food_name || '').toLowerCase())
      )
    }

    return (ingredient.food_name || '').toLowerCase().startsWith(query.toLowerCase())
  })

  return [
    ...startsWithQuery.sort((a, b) => (a.food_name || '').localeCompare(b.food_name || '')),
    ...containsQuery.sort((a, b) => (a.food_name || '').localeCompare(b.food_name || '')),
  ]
}

const searchItemsWithNutrients = async (query: string) => {
  const cleanedQuery = sanitizeNaturalLanguageQuery(query)

  // Everything meaningful was stripped (e.g. a description holding only a
  // pasted attachment). An empty query is itself a 400, so skip the request.
  if (!cleanedQuery) {
    return [] as NutritionixItemWithNutrients[]
  }

  // Nutritionix answers 500 (not 400) when the query holds nothing it can
  // parse as food — a lone "." or "-" left in the meal description is enough.
  // Such a query can never return anything, so don't spend a request on it.
  if (!/[a-z0-9]/i.test(cleanedQuery)) {
    return []
  }

  const cacheKey = `nutrients:${cleanedQuery.toLowerCase()}`
  const cached = readNutrientsCache(cacheKey)

  if (cached) {
    return cached
  }

  const response = await request<NutritionixWithNutrientsResults>(
    {
      method: 'POST',
      url: `/natural/nutrients`,
      data: {
        query: cleanedQuery,
      },
    },
    { showNotFoundWarnings: false },
  )

  const foods = get(response, 'foods', []).filter(
    (item: NutritionixItemWithNutrients | undefined) => item,
  ) as NutritionixItemWithNutrients[]

  // Only a real answer is worth keeping — a failed lookup should be retried
  // the next time the ingredient is shown.
  if (response) {
    writeNutrientsCache(cacheKey, foods)
  }

  return foods
}

const getItemsByBarcode = async ({ barcode }: { barcode: string }) => {
  const response = await request<NutritionixWithNutrientsResults>({
    method: 'GET',
    url: `/search/item?upc=${barcode}`,
  })

  return get(response, 'foods', []).filter(
    (item: NutritionixItemWithNutrients | undefined) => item,
  ) as NutritionixItemWithNutrients[]
}

const getBrandedItemById = async (nixItemId: string) => {
  const cacheKey = `item:${nixItemId}`
  const cached = readNutrientsCache(cacheKey)

  if (cached) {
    return cached[0]
  }

  const response = await request<NutritionixWithNutrientsResults>({
    method: 'GET',
    url: `/search/item?nix_item_id=${nixItemId}`,
  })

  const foods = get(response, 'foods', []).filter(
    (item: NutritionixItemWithNutrients | undefined) => item,
  ) as NutritionixItemWithNutrients[]

  if (response) {
    writeNutrientsCache(cacheKey, foods)
  }

  return foods[0] as NutritionixItemWithNutrients | undefined
}

export default {
  searchItems,
  searchItemsWithNutrients,
  getItemsByBarcode,
  getBrandedItemById,
}
